import { useContext } from 'react';
import { FormContext } from '~/components/forms/FormContext';
import FormLogo from '~/components/icons/FormLogo';

/**
 * Calculate relative luminance for a color
 * @param {string} hex - Hex color code
 * @returns {number} Relative luminance value
 */
const getLuminance = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const [rs, gs, bs] = [r, g, b].map(c =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );

  // Calculate luminance
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Check if two colors are similar
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @param {number} threshold - Similarity threshold (0-765, default 50)
 * @returns {boolean} True if colors are similar
 */
const areColorsSimilar = (color1, color2, threshold = 50) => {
  // Remove # if present
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');

  // Convert to RGB
  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);

  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);

  // Calculate Euclidean distance in RGB space
  const distance = Math.sqrt(
    Math.pow(r1 - r2, 2) +
    Math.pow(g1 - g2, 2) +
    Math.pow(b1 - b2, 2)
  );

  return distance <= threshold;
};

/**
 * Calculate contrast ratio between two colors
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @returns {number} Contrast ratio
 */
const getContrastRatio = (color1, color2) => {
  // Ensure we have valid hex colors
  const hex1 = color1.startsWith('#') ? color1 : `#${color1}`;
  const hex2 = color2.startsWith('#') ? color2 : `#${color2}`;

  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Get WCAG compliant color prioritizing brand color
 * @param {string} backgroundColor - Background color hex
 * @param {string} brandColor - Brand color hex (default: #2C87F3)
 * @returns {string} Brand color if it meets WCAG AA, otherwise black or white
 */
const getAccessibleColor = (backgroundColor, brandColor = '#2C87F3') => {
  const WHITE = '#FFFFFF';
  const BLACK = '#000000';
  // Use 3.0 for large text/graphics (WCAG AA)
  // Logo graphics can use the more lenient 3:1 ratio
  const WCAG_AA_LARGE = 3.0;

  // If background is similar to brand color, always use white for branding consistency
  // Threshold of 50 means colors within ~20% RGB difference are considered similar
  if (areColorsSimilar(backgroundColor, brandColor, 50)) {
    return WHITE;
  }

  // Get background luminance to check if it's very dark or very light
  const bgLuminance = getLuminance(backgroundColor);

  // If background is very dark (luminance < 0.1), use white for maximum contrast
  if (bgLuminance < 0.1) {
    return WHITE;
  }

  // If background is very light (luminance > 0.9), prefer brand color
  if (bgLuminance > 0.9) {
    const brandContrast = getContrastRatio(backgroundColor, brandColor);
    // Use brand color if it meets minimum contrast, otherwise fall back to black
    return brandContrast >= WCAG_AA_LARGE ? brandColor : BLACK;
  }

  // For mid-tone backgrounds, prefer brand color if it has sufficient contrast
  const brandContrast = getContrastRatio(backgroundColor, brandColor);
  if (brandContrast >= WCAG_AA_LARGE) {
    return brandColor;
  }

  // Only fall back to black or white if brand color doesn't meet standards
  const whiteContrast = getContrastRatio(backgroundColor, WHITE);
  const blackContrast = getContrastRatio(backgroundColor, BLACK);

  return whiteContrast > blackContrast ? WHITE : BLACK;
};

/**
 * Breely's mark. It is rendered outside the custom CSS scope on purpose, so
 *  that a business's own stylesheet has no selector that can reach it, and it
 *  takes the form as a prop for the same reason: outside the scope it is also
 *  outside FormContextProvider.
 */
const PoweredBy = ({ className, showOnlyFree, form: formProp }) => {
  const contextForm = useContext(FormContext)?.form;
  const form = formProp || contextForm;
  const isFree = form?.business?.plan === 'free';
  const formColor = form?.settings?.styles?.headerColor || '#2C87F3';
  const hasFullPageImage = form?.settings?.styles?.fullPageImage;

  // Determine background color for contrast calculation
  // If hasFullPageImage is true, use the header color as background
  // Otherwise, default to a light background color
  const backgroundColor = (hasFullPageImage && hasFullPageImage !== '') ? formColor : '#f9f9f9';

  // Get WCAG compliant logo color
  const logoFill = getAccessibleColor(backgroundColor, '#2C87F3');

  // Debug logging (remove in production)
  /*
  console.log('hasFullPageImage:', hasFullPageImage);
  console.log('formColor:', formColor);
  console.log('Background:', backgroundColor);
  console.log('Luminance - Background:', getLuminance(backgroundColor));
  console.log('Luminance - White:', getLuminance('#FFFFFF'));
  console.log('Luminance - Black:', getLuminance('#000000'));
  console.log('Brand contrast:', getContrastRatio(backgroundColor, '#2C87F3'));
  console.log('White contrast:', getContrastRatio(backgroundColor, '#FFFFFF'));
  console.log('Black contrast:', getContrastRatio(backgroundColor, '#000000'));
  console.log('Selected color:', logoFill);
  */

  if (!isFree && showOnlyFree) {
    return null;
  }

  let encodedBusinessName = '';
  try {
      encodedBusinessName = encodeURIComponent(btoa(form?.business?.name))
  } catch (e) {
      // ignore
  }

  if (isFree) {
    return (
        <a
            href={`https://www.breely.com/?utm_source=poweredby-free&bn=${encodedBusinessName}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`breely-branding gap-2 text-center flex justify-center items-center text-sm no-underline ${className}`}
            style={{ color: logoFill }}
            aria-label="Powered by Breely - Create your own form"
        >
            <span className="uppercase text-[12px]">Powered by</span>
            <FormLogo className="w-[80px]" fill={logoFill} aria-hidden="true" />
        </a>
    );
  } else {
    return (
      <div className={`breely-branding text-center text-sm text-gray-500 ${className}`}>
        Powered by{' '}
        <a
          href={`https://www.breely.com/?utm_source=poweredby&bn=${encodedBusinessName}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-center !text-gray-500"
        >
          Breely
        </a>
      </div>
    );
  }
};

export default PoweredBy;
